import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * useResizableWidth — manages a draggable panel width with clamping,
 * localStorage persistence, double-click-to-reset, and window-resize
 * clamping.
 *
 * By default this preserves the Markdown viewer sidebar behaviour: the drag
 * handle sits on the panel's left edge, so dragging left widens and dragging
 * right narrows. Right-edge handles can opt into the inverse direction.
 *
 * Uses Pointer Events (mouse + touch) with pointer capture so the drag
 * continues even if the cursor leaves the handle element.
 *
 * @param opts.initial    Default width in px (default 480)
 * @param opts.min        Minimum width in px   (default 320)
 * @param opts.max        Maximum width in px   (default 800)
 * @param opts.storageKey If set, persist/restore width via localStorage
 * @param opts.minPeer    Minimum width to reserve for the neighbouring
 *                        panel (chat area) when the window shrinks
 *                        (default 400)
 * @param opts.handleSide Edge on which the drag handle sits (default "left")
 */
export function useResizableWidth(opts: {
  initial?: number;
  min?: number;
  max?: number;
  storageKey?: string;
  minPeer?: number;
  handleSide?: "left" | "right";
}) {
  const min = opts.min ?? 320;
  const max = opts.max ?? 800;
  const initial = opts.initial ?? 480;
  const minPeer = opts.minPeer ?? 400;
  const storageKey = opts.storageKey;
  const handleSide = opts.handleSide ?? "left";

  const readInitialWidth = useCallback(() => {
    if (storageKey && typeof window !== "undefined") {
      const saved = window.localStorage.getItem(storageKey);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!Number.isNaN(parsed)) {
          return Math.max(min, Math.min(parsed, max));
        }
      }
    }
    return initial;
  }, [initial, max, min, storageKey]);

  const [width, setWidth] = useState<number>(readInitialWidth);

  // A single resize controller can serve multiple mutually-exclusive panels.
  // When its active panel changes, restore that panel's own persisted width
  // before paint rather than carrying the previous panel's width across.
  const configKey = `${storageKey ?? ""}\u0000${initial}\u0000${min}\u0000${max}`;
  const previousConfigKey = useRef(configKey);
  useLayoutEffect(() => {
    if (previousConfigKey.current === configKey) return;
    previousConfigKey.current = configKey;
    setWidth(readInitialWidth());
  }, [configKey, readInitialWidth]);

  const configRef = useRef({ initial, min, max, minPeer, storageKey, handleSide });
  configRef.current = { initial, min, max, minPeer, storageKey, handleSide };

  // ── Drag state (ref so pointermove handler stays stable) ──────────────
  const dragState = useRef<{ startX: number; startWidth: number } | null>(
    null,
  );

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    dragState.current = { startX: e.clientX, startWidth: 0 };
    // Read the current width from the DOM element's bounding rect so we
    // don't need a stale closure over `width`.
    const el = e.currentTarget as HTMLElement;
    const rect = el.parentElement?.getBoundingClientRect();
    if (rect) dragState.current.startWidth = rect.width;
    el.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragState.current) return;
      // Left-edge handles widen when dragged left; right-edge handles widen
      // when dragged right.
      const delta = e.clientX - dragState.current.startX;
      const { handleSide: currentHandleSide, min: currentMin, max: currentMax } = configRef.current;
      const newWidth = dragState.current.startWidth
        + (currentHandleSide === "right" ? delta : -delta);
      setWidth(Math.max(currentMin, Math.min(newWidth, currentMax)));
    },
    [],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!dragState.current) return;
      dragState.current = null;
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      // Persist via functional update so we read the latest width
      const currentStorageKey = configRef.current.storageKey;
      if (currentStorageKey) {
        setWidth((w) => {
          try {
            window.localStorage.setItem(currentStorageKey, String(w));
          } catch {
            /* localStorage may be unavailable (private mode) — ignore */
          }
          return w;
        });
      }
    },
    [],
  );

  // Double-click the handle to reset to initial width
  const onDoubleClick = useCallback(() => {
    setWidth(configRef.current.initial);
  }, []);

  // ── Window-resize clamping ─────────────────────────────────────────────
  // If the viewport shrinks so that width + minPeer > window.innerWidth,
  // shrink the sidebar to keep at least `minPeer` px for the chat area.
  useEffect(() => {
    const handleResize = () => {
      const { min: currentMin, minPeer: currentMinPeer } = configRef.current;
      const maxAllowed = window.innerWidth - currentMinPeer;
      setWidth((w) => {
        if (maxAllowed < currentMin) return currentMin; // viewport too small — collapse to min
        return Math.min(w, maxAllowed);
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return useMemo(
    () => ({ width, onPointerDown, onPointerMove, onPointerUp, onDoubleClick }),
    [onDoubleClick, onPointerDown, onPointerMove, onPointerUp, width],
  );
}
